import { z } from "zod/v4-mini";

import { DeviceType, deviceTypeSchema } from "@/models/MedicalDeviceModel.ts";
import { CamelCaseObject } from "@/types/case";
import { Schema, cz } from "@/utils/zodUtils.ts";

/***********************
 *    Insulin types    *
 ***********************/

export const insulinTypeSchema = z.literal(["long", "short"]);
export type InsulinType = z.infer<typeof insulinTypeSchema>;
export const insulinReasonSchema = z.literal(["bolus", "basal"]);
export type InsulinReason = z.infer<typeof insulinReasonSchema>;
export const insulinPeriodSchema = z.literal(["breakfast", "lunch", "dinner"]);
export type InsulinPeriod = z.infer<typeof insulinPeriodSchema>;
export const insulinMethodSchema = z.literal(["pen", "pump"]);
export type InsulinMethod = z.infer<typeof insulinMethodSchema>;

export type Insulin = {
  id: string;
  patientId: string;
  date: string;
  deviceId?: string;
  deviceKind?: DeviceType;

  quantity: number;
  type: InsulinType;
  reason: InsulinReason;
  method: InsulinMethod;
  period?: InsulinPeriod;
  correction?: boolean;
  bolusSettings?: BolusSettings;
};

export type DiscreteBasalInsulin = Omit<Insulin, "type" | "reason"> & {
  type: "long";
  reason: "basal";
};

export type ContinuousBasalInsulin = Omit<Insulin, "type" | "reason"> & {
  type: "short";
  reason: "basal";
};

export type BolusInsulin = Omit<Insulin, "type" | "reason"> & {
  type: "short";
  reason: "bolus";
};

export type SplitInsulinData = {
  continuous: ContinuousBasalInsulin[];
  discrete: DiscreteBasalInsulin[];
  bolus: BolusInsulin[];
};

export type ManualBolusSettings = {
  type: "manual";
  bolusId: string;
  foodInsulin: number;
  suggestedFoodInsulin?: number;
  suggestedTotalInsulin?: number;
  iob?: number;
};

export type CarbsBolusSettings = {
  type: "carbs";
  bolusId: string;
  foodInsulin: number;
  suggestedFoodInsulin?: number;
  suggestedTotalInsulin?: number;
  carbs?: number;
  carbsRatio?: number;
  iob?: number;
};

export type CorrectionBolusSettings = {
  type: "correction";
  bolusId: string;
  correctionInsulin: number;
  suggestedCorrectionInsulin?: number;
  suggestedTotalInsulin?: number;
  glycemia?: number;
  insulinSensitivity?: number;
  targetGlycemia?: number;
  iob?: number;
};

export type CarbsCorrectionBolusSettings = {
  type: "carbs_and_correction";
  bolusId: string;
  foodInsulin: number;
  suggestedFoodInsulin?: number;
  carbs?: number;
  carbsRatio?: number;
  correctionInsulin: number;
  suggestedCorrectionInsulin?: number;
  suggestedTotalInsulin?: number;
  glycemia?: number;
  insulinSensitivity?: number;
  targetGlycemia?: number;
  iob?: number;
};

export type BolusSettings =
  | ManualBolusSettings
  | CarbsBolusSettings
  | CorrectionBolusSettings
  | CarbsCorrectionBolusSettings;

const rawBolusSettingsSchema = cz.camelCaseObject({
  bolus_id: cz.id(),
  food_insulin_input_method: z.literal(["manual", "carbs", "none"]),
  food_insulin: z.number(),
  food_insulin_suggested: cz.optional(z.number()),
  correction_insulin_input_method: z.literal(["blood_glucose", "none"]),
  correction_insulin: z.number(),
  correction_insulin_suggested: cz.optional(z.number()),
  carbs: cz.optional(z.number()),
  carbs_ratio: cz.optional(z.number()),
  glycemia: cz.optional(z.number()),
  insulin_sensitivity: cz.optional(z.number()),
  target_glycemia: cz.optional(z.number()),
  iob: cz.optional(z.number()),
});

const transformRawBolusSettings = (
  data: CamelCaseObject<z.infer<typeof rawBolusSettingsSchema>>,
): BolusSettings | undefined => {
  if (data.foodInsulinInputMethod === "manual") {
    return {
      type: "manual",
      bolusId: data.bolusId,
      foodInsulin: data.foodInsulin,
      suggestedFoodInsulin: data.foodInsulinSuggested,
      suggestedTotalInsulin: data.foodInsulinSuggested,
    } satisfies ManualBolusSettings;
  }
  const carbsSettings =
    data.foodInsulinInputMethod === "carbs"
      ? {
          bolusId: data.bolusId,
          foodInsulin: data.foodInsulin,
          suggestedFoodInsulin: data.foodInsulinSuggested,
          suggestedTotalInsulin: data.foodInsulinSuggested,
          carbs: data.carbs,
          carbsRatio: data.carbsRatio,
          iob: data.iob,
        }
      : undefined;
  const correctionSettings =
    data.correctionInsulinInputMethod === "blood_glucose"
      ? {
          bolusId: data.bolusId,
          correctionInsulin: data.correctionInsulin,
          suggestedCorrectionInsulin: data.correctionInsulinSuggested,
          suggestedTotalInsulin: data.correctionInsulinSuggested,
          glycemia: data.glycemia,
          insulinSensitivity: data.insulinSensitivity,
          targetGlycemia: data.targetGlycemia,
          iob: data.iob,
        }
      : undefined;

  if (carbsSettings && correctionSettings) {
    const suggestedTotalInsulinValue = (data.foodInsulinSuggested ?? 0) + (data.correctionInsulinSuggested ?? 0);
    const suggestedTotalInsulin = suggestedTotalInsulinValue > 0 ? suggestedTotalInsulinValue : undefined;
    return {
      type: "carbs_and_correction",
      ...carbsSettings,
      ...correctionSettings,
      suggestedTotalInsulin,
    };
  } else if (carbsSettings) {
    return {
      type: "carbs",
      ...carbsSettings,
    };
  } else if (correctionSettings) {
    return {
      type: "correction",
      ...correctionSettings,
    };
  }

  return undefined;
};

export const insulinSchema: Schema<Insulin> = z.pipe(
  cz.camelCaseObject({
    id: cz.id(),
    patient_id: z.string(),
    date: cz.datetime(),
    device_id: cz.optional(cz.id()),
    device_kind: cz.optional(deviceTypeSchema),
    quantity: z.number(),
    type: insulinTypeSchema,
    reason: insulinReasonSchema,
    method: insulinMethodSchema,
    period: cz.optional(insulinPeriodSchema),
    correction: cz.optional(z.boolean()),
    bolus_settings: cz.optional(rawBolusSettingsSchema),
  }),
  z.transform(({ bolusSettings, ...data }) => {
    const transformedBolusSettings = bolusSettings ? transformRawBolusSettings(bolusSettings) : undefined;
    if (transformedBolusSettings) {
      return {
        ...data,
        bolusSettings: transformedBolusSettings,
      };
    } else {
      return data;
    }
  }),
);

/***********************
 *   Insulin helpers   *
 ***********************/

export const getInsulinTypeTranslation = (type: InsulinType): string => {
  switch (type) {
    case "long":
      return "models.insulinType.slow";
    case "short":
      return "models.insulinType.fast";
  }
};

export const getInsulinReasonTranslation = (reason: InsulinReason): string => {
  switch (reason) {
    case "bolus":
      return "models.insulinReason.bolus";
    case "basal":
      return "models.insulinReason.basal";
  }
};
